import { useState } from 'react';
import { Mail } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Callout } from '../../components/ui/Callout';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { Spinner } from '../../components/ui/Spinner';
import { useToast } from '../../contexts/ToastContext';
import { extractErrorMessage } from '../../api/errors';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { invalidColourErrors, resolveStyle, sameDesign } from '../../lib/email-design';
import {
  useDeactivateEmailTheme,
  useEmailPreview,
  useEmailTheme,
  useSaveEmailTheme,
  useSendTestEmail,
  type EmailDesign,
} from '../../queries/brandEmailTheme';
import { EmailDesignForm } from './EmailDesignForm';
import { EmailGallery } from './EmailGallery';
import { EmailVersionHistory } from './EmailVersionHistory';

const PREVIEW_DEBOUNCE_MS = 400;

interface EmailDesignPanelProps {
  canWrite: boolean;
  brandName: string;
}

function ChecksList({ errors, warnings }: { errors: string[]; warnings: string[] }) {
  if (errors.length === 0 && warnings.length === 0) return null;
  return (
    <div className="space-y-2">
      {errors.length > 0 && (
        <Callout variant="error" title="Fix these before saving">
          <ul className="list-disc space-y-1 pl-4">{errors.map((e) => <li key={e}>{e}</li>)}</ul>
        </Callout>
      )}
      {warnings.length > 0 && (
        <Callout variant="warning" title="Hard to read">
          <ul className="list-disc space-y-1 pl-4">{warnings.map((w) => <li key={w}>{w}</li>)}</ul>
        </Callout>
      )}
    </div>
  );
}

/**
 * Brand Settings > Email (ADR-0012): the colours and letterhead every email of
 * the acting brand is built from, with a live gallery of each email rendered
 * from the unsaved design. A save is a new version and goes live at once.
 */
export function EmailDesignPanel({ canWrite, brandName }: EmailDesignPanelProps) {
  const { data, isLoading, error } = useEmailTheme();
  const save = useSaveEmailTheme();
  const deactivate = useDeactivateEmailTheme();
  const sendTest = useSendTestEmail();
  const toast = useToast();
  const [draft, setDraft] = useState<EmailDesign | null>(null);
  const [confirming, setConfirming] = useState<'save' | 'deactivate' | null>(null);
  const [sentTo, setSentTo] = useState<string | null>(null);

  const original: EmailDesign | null = data?.active
    ? { tokens: data.active.tokens, identity: data.active.identity, style: resolveStyle(data.active.style, data.starter.style) }
    : null;
  const design = draft ?? original;
  const localErrors = design ? invalidColourErrors(design.tokens) : [];
  const debounced = useDebouncedValue(design, PREVIEW_DEBOUNCE_MS);
  // The server refuses a malformed colour, so only a well-formed design is previewed.
  const previewDesign = debounced && invalidColourErrors(debounced.tokens).length === 0 ? debounced : null;
  const preview = useEmailPreview(previewDesign);

  if (isLoading) return <Spinner />;
  if (error || !data) return <Callout variant="error" title="Could not load the email design">{extractErrorMessage(error, '')}</Callout>;

  const previewError = preview.error ? extractErrorMessage(preview.error, 'The preview failed.') : null;
  const checking = !sameDesign(design, debounced) || preview.isFetching;
  const errors = localErrors.length > 0 ? localErrors : (preview.data?.checks.errors ?? []);
  const warnings = localErrors.length > 0 ? [] : (preview.data?.checks.warnings ?? []);
  const dirty = draft !== null && (original === null || !sameDesign(draft, original));
  const canSave = dirty && errors.length === 0 && !previewError && !checking && !save.isPending;
  const nextVersion = (data.versions[0]?.version ?? 0) + 1;

  const submitSave = () =>
    design &&
    save.mutate(design, {
      onSuccess: ({ theme }) => {
        setDraft(null);
        setConfirming(null);
        toast.success(`Email design saved as version ${theme.version}. Every ${brandName} email uses it from now on.`);
      },
      onError: (err) => {
        setConfirming(null);
        toast.error(extractErrorMessage(err, 'Failed to save the email design.'));
      },
    });

  const submitDeactivate = () =>
    deactivate.mutate(undefined, {
      onSuccess: () => {
        setDraft(null);
        setConfirming(null);
        toast.success(`The email design is off. ${brandName} is back on its old emails.`);
      },
      onError: (err) => {
        setConfirming(null);
        toast.error(extractErrorMessage(err, 'Failed to switch the design off.'));
      },
    });

  const submitTest = (key: string) =>
    design &&
    sendTest.mutate(
      { key, ...design },
      {
        onSuccess: ({ sentTo: to }) => {
          setSentTo(to);
          toast.success(`Test email sent to ${to}.`);
        },
        onError: (err) => toast.error(extractErrorMessage(err, 'Failed to send the test email.')),
      },
    );

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Mail className="h-4 w-4" /> Email
        </CardTitle>
        <CardDescription>
          The colours, fonts and letterhead every {brandName} email is built from: account emails, receipts, tips, campaigns and series. Edit
          the design and check every email on the right before saving. A save goes live at once, and older versions can be restored.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <p className="text-xs text-muted-foreground">
          The brand name, site, sender address and ABN in the footer come from the brand registry. Edit them under Identity &amp; look,
          Email senders and Business &amp; compliance on this page.
        </p>

        {!design ? (
          <Callout variant="info" title="This brand is on its old emails">
            <p>
              {brandName} has no live email design, so it sends its old emails.
              {data.versions.length > 0 ? ' Restore an earlier version below, or start again.' : ''}
            </p>
            {canWrite && (
              <Button size="sm" className="mt-3" onClick={() => setDraft(data.starter)}>
                Start from the starter design
              </Button>
            )}
          </Callout>
        ) : (
          <div className="grid grid-cols-1 gap-6 xl:grid-cols-[minmax(0,1fr)_minmax(0,640px)]">
            <div className="space-y-4">
              <EmailDesignForm design={design} fonts={data.fonts} canWrite={canWrite} onChange={setDraft} />
              {!original && <Callout variant="info" title="Not live yet">Nothing changes for customers until this design is saved.</Callout>}
              <ChecksList errors={errors} warnings={warnings} />
              {canWrite && (
                <div className="flex flex-wrap items-center gap-2">
                  <Button onClick={() => setConfirming('save')} disabled={!canSave}>
                    {save.isPending ? 'Saving…' : dirty ? (checking ? 'Checking…' : `Save as version ${nextVersion}`) : 'No changes'}
                  </Button>
                  {draft && (
                    <Button variant="ghost" onClick={() => setDraft(null)}>
                      Discard changes
                    </Button>
                  )}
                  {data.active && (
                    <Button variant="destructive" className="ml-auto" onClick={() => setConfirming('deactivate')}>
                      Switch the design off
                    </Button>
                  )}
                </div>
              )}
            </div>
            <EmailGallery
              emails={preview.data?.emails}
              loading={checking}
              error={previewError}
              canWrite={canWrite && localErrors.length === 0}
              sending={sendTest.isPending}
              sentTo={sentTo}
              onSendTest={submitTest}
            />
          </div>
        )}

        <EmailVersionHistory versions={data.versions} fonts={data.fonts} starterStyle={data.starter.style} canWrite={canWrite} brandName={brandName} onRestored={() => setDraft(null)} />
      </CardContent>

      <ConfirmDialog
        open={confirming === 'save'}
        title={`Save the email design as version ${nextVersion}?`}
        description={`Every email this brand sends will use this design from now on. ${brandName} customers see it on the next email they receive.`}
        confirmLabel="Save and make live"
        busy={save.isPending}
        onConfirm={submitSave}
        onOpenChange={(open) => !open && !save.isPending && setConfirming(null)}
      >
        {warnings.length > 0 && <p className="text-amber-600">{warnings.length} colour{warnings.length === 1 ? ' is' : 's are'} hard to read.</p>}
      </ConfirmDialog>
      <ConfirmDialog
        open={confirming === 'deactivate'}
        title="Switch the email design off?"
        description={`${brandName} goes back to its old emails straight away, and they do not follow this design. This is a safety switch for when the design is broken, not a place to stay. Every version stays in the history and can be restored.`}
        confirmLabel="Switch it off"
        destructive
        busy={deactivate.isPending}
        onConfirm={submitDeactivate}
        onOpenChange={(open) => !open && !deactivate.isPending && setConfirming(null)}
      />
    </Card>
  );
}
